
export interface Accessors<CalendarEvent> {
  start( event: CalendarEvent ): Date;
  end( event: CalendarEvent ): Date;
}

export interface SlotRange {
  start: number;
  end: number;
  startDate: Date;
  endDate: Date;
  top: number;
  height: number;
}

export interface SlotMetrics {
  getRange( start: Date, end: Date ): SlotRange;
}

export interface Style {
  top: number;
  height: number;
  left?: number;
  width?: string;
  xOffset: number;
}

export interface StyledEvent<CalendarEvent> {
  event: CalendarEvent;
  style: Style;
  friends?: StyledEvent<CalendarEvent>[];
  idx?: number;
  size?: number;
}

class ProxyEvent<CalendarEvent> {
  start: number;
  end: number;
  startMs: number;
  endMs: number;
  top: number;
  height: number;

  rows?: ProxyEvent<CalendarEvent>[];
  leaves?: ProxyEvent<CalendarEvent>[];
  container?: ProxyEvent<CalendarEvent>;
  row?: ProxyEvent<CalendarEvent>;

  data: CalendarEvent;

  constructor( data: CalendarEvent, accessors: Accessors<CalendarEvent>, slotMetrics: SlotMetrics ) {
    const {
      start,
      end,
      startDate,
      endDate,
      top,
      height
    } = slotMetrics.getRange( accessors.start( data ), accessors.end( data ) );

    this.start = start;
    this.end = end;
    this.startMs = +startDate;
    this.endMs = +endDate;
    this.top = top;
    this.height = height;
    this.data = data;
  }

  /* width logic */
  get _width(): number {
    if ( this.rows ) {
      const columns =
        this.rows.reduce( ( max, row ) => {
          return Math.max( max, ( row.leaves?.length ?? 0 ) + 1 );
        }, 0 ) + 1;

      return 100 / columns;
    }

    if ( this.leaves ) {
      const availableWidth = 100 - this.container!._width;
      return availableWidth / ( this.leaves.length + 1 );
    }

    return this.row!._width;
  }

  get width(): number {
    const noOverlap = this._width;
    const overlap = Math.min( 100, this._width * 1.7 );

    if ( this.rows ) {return overlap;}
    if ( this.leaves ) {return this.leaves.length > 0 ? overlap : noOverlap;}

    const leaves = this.row!.leaves!;
    const index = leaves.indexOf( this );
    return index === leaves.length - 1 ? noOverlap : overlap;
  }

  get xOffset(): number {
    if ( this.rows ) {return 0;}
    if ( this.leaves ) {return this.container!._width;}

    const row = this.row!;
    const index = row.leaves!.indexOf( this ) + 1;
    return row.xOffset + index * row._width;
  }
}

/* ============================
    FONCTIONS INTERNES
============================ */

function onSameRow<CalendarEvent>(
  a: ProxyEvent<CalendarEvent>,
  b: ProxyEvent<CalendarEvent>,
  minimumStartDifference: number
): boolean {
  return (
    Math.abs( b.start - a.start ) < minimumStartDifference ||
    ( b.start > a.start && b.start < a.end )
  );
}

function sortByRender<CalendarEvent>(
  events: ProxyEvent<CalendarEvent>[]
): ProxyEvent<CalendarEvent>[] {


  const sortedByTime = events.sort( ( a, b ) => {
    return a.startMs < b.startMs ? 1 : -1;
  } );

  // const sortedByTime = sortBy( events, [
  //   'startMs',
  //   ( e: ProxyEvent<CalendarEvent> ) => {return -e.endMs;},
  // ] );

  const sorted: ProxyEvent<CalendarEvent>[] = [];

  while ( sortedByTime.length > 0 ) {
    const event = sortedByTime.shift()!;
    sorted.push( event );

    let foundNextGroup = false;

    for ( let i = 0; i < sortedByTime.length && !foundNextGroup; i++ ) {
      const test = sortedByTime[ i ];

      if ( !( event.endMs > test.startMs ) ) {
        if ( i > 0 ) {
          const next = sortedByTime.splice( i, 1 )[ 0 ];
          sorted.push( next );
        }
        foundNextGroup = true;
      }
    }
  }

  return sorted;
}

/* ============================
    EXPORT PRINCIPAL
============================ */

export default function overlapDefault<CalendarEvent>( {
  events,
  minimumStartDifference,
  slotMetrics,
  accessors,
}: {
  events: readonly CalendarEvent[];
  minimumStartDifference: number;
  slotMetrics: SlotMetrics;
  accessors: Accessors<CalendarEvent>;
} ): StyledEvent<CalendarEvent>[] {

  const proxies = events.map(
    e => {return new ProxyEvent( e, accessors, slotMetrics );}
  );

  const eventsInRenderOrder = sortByRender( proxies );
  const containerEvents: ProxyEvent<CalendarEvent>[] = [];

  for ( let i = 0; i < eventsInRenderOrder.length; i++ ) {
    const event = eventsInRenderOrder[ i ];

    // eslint-disable-next-line init-declarations
    let container: ProxyEvent<CalendarEvent> | undefined;

    for ( let c = 0; c < containerEvents.length && !container; c++ ) {
      const candidate = containerEvents[ c ];
      if (
        candidate.end > event.start ||
        Math.abs( event.start - candidate.start ) < minimumStartDifference
      ) {
        container = candidate;
      }
    }

    if ( !container ) {
      event.rows = [];
      containerEvents.push( event );
    } else {
      event.container = container;
      let row: ProxyEvent<CalendarEvent> | null = null;

      for ( let j = container.rows!.length - 1; j >= 0 && !row; j-- ) {
        const candidate = container.rows![ j ];
        if ( onSameRow( candidate, event, minimumStartDifference ) ) {
          row = candidate;
        }
      }

      if ( row ) {
        row.leaves!.push( event );
        event.row = row;
      } else {
        event.leaves = [];
        container.rows!.push( event );
      }
    }
  }

  return eventsInRenderOrder.map( event => {return {
    event: event.data,
    style: {
      top: event.top,
      height: event.height,
      width: String( event.width ),
      xOffset: Math.max( 0, event.xOffset ),
    } as Style,
  } as StyledEvent<CalendarEvent> ;} );
}